/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {Fragment} from 'react'
import {FormattedMessage} from 'react-intl'

import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {
    Alert,
    Button,
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import {colors} from '../../../constants'
import ChangePasswordFields from './change-password-feilds'
import Link from '../../../components/link'
import {capitalizeFirstLetter} from '../../../utils'

const ChangePasswordForm = ({submitForm, clickSignIn = noop, form}) => {
    return (
        <Fragment>
            <Stack justify="center" align="center" marginBottom="40px">
                <Text
                    align="center"
                    fontSize="40px"
                    lineHeight="47px"
                    fontFamily="Raleway"
                    fontWeight="300"
                >
                    <FormattedMessage
                        defaultMessage="Reset Password"
                        id="reset_password_form.title.reset_password"
                    />
                </Text>
            </Stack>

            <form
                id="change-password-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
            >
                <Stack spacing="20px">
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {capitalizeFirstLetter(
                                    form.formState.errors.global.message
                                )}
                            </Text>
                        </Alert>
                    )}
                    <ChangePasswordFields form={form} />

                    <Stack spacing={6}>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            border="1px solid"
                            background="black"
                            backgroundColor="black"
                            borderRadius="20px"
                        >
                            <Button
                                type="submit"
                                onClick={() => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting}
                                width="100%"
                                margin="0px 2px"
                                borderRadius="20px"
                                display="flex"
                                gap="10px"
                                padding="0 14px"
                                justifyContent="left"
                                fontSize="14px"
                                fontWeight="300"
                                background={colors.black}
                                backgroundColor={colors.black}
                                _hover={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _active={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _disabled={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Reset Password"
                                    id="reset_password_form.button.reset_password"
                                />
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.16663 7H12.8333M12.8333 7L6.99996 1.16667M12.8333 7L6.99996 12.8333"
                                        stroke={colors.white}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Button>
                            <Link
                                buttonStyles={{
                                    color: colors.white,
                                    fontSize: '14px',
                                    fontWeight: '300',
                                    textDecoration: 'underline',
                                    _hover: {
                                        textDecoration: 'underline'
                                    },
                                    _focus: {
                                        background: colors.black,
                                        backgroundColor: colors.black
                                    },
                                    width: 'fit-content',
                                    marginRight: '20px',
                                    background: colors.black,
                                    backgroundColor: colors.black,
                                    height: '15px'
                                }}
                                type="tertiary"
                                text="Sign in"
                                onLinkClick={clickSignIn}
                            />
                        </Flex>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

ChangePasswordForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object
}

export default ChangePasswordForm
